import React, { useState, useReducer, useEffect } from "react";
import { useTranslation } from "react-i18next";
//import { useNavigate } from "react-router-dom";

// Components
import Spinner from "../Components/Spinner";
import ToastError from "../Components/ToastError";
import SearchInputField from "../Components/Forms/SearchInputField";
import SearchInputDocument from "../Components/Forms/SearchInputDocument";
import LongTable from '../Components/Tables/LongTable';

// Icons
import { AiOutlineSearch } from "react-icons/ai";

// Services
import { storage } from "../../Services/storage.service";
import { clientService } from "../../Services/client.service";

// Constants
import { storageKeys } from "../../Constants/storage.key";
import { BiChevronsLeft, BiChevronsRight } from "react-icons/bi";


const filterReducer = (state, event) => {
  if (event.reset) {
    return { clientName: "", document: "", plate: "" };
  }
  return {
    ...state,
    [event.name]: event.value,
  };
};


const ClientSearch = () => {
  const { t } = useTranslation();
  
 // const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [filter, setFilter] = useReducer(filterReducer, {});
  const [clientList, setClientList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const listColumns = [t("CLIENT.SOCIAL_NAME"), t("CLIENT.TRADE_NAME"), t("COMMON.CPF_CNPJ"), t("COMMON.PLATE"), t("CLIENT.PLATE_NICKNAME"), t("CLIENT.CONTRACT_STATUS")];
  const listFieldColumns = ["socialName", "tradeName", "taxId", "licensePlate", "plateNickname", "contractStatusDescription"];
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25); // Começa com o valor da API
     // eslint-disable-next-line
  const [totalPages, setTotalPages] = useState(1);
  const [settings] = useState(storage.get(storageKeys.settings)); 


  const onSearch = async () => {
    
    var page = 1
    setIsLoading(true);
    setCurrentPage(page)

    try {
      var payload = {
        "regionalId": settings.regionalId ?? 53,
        "clientLicensedId": 0,
        "partialName": filter?.clientName || "",
        "doc": (filter?.document || "").replace(/([^\w]+|\s+)/g, ''),
        "licensePlate": filter?.plate || "",
        "pageNumber": page,  // Página solicitada
        "pageSize": 10, // Itens por página
      };

      var response = await clientService.search(payload);
      if (response) {
        const mapped = (response.items || []).map(c => ({
          clientId: c.customerId,
          socialName: c.name,
          tradeName: c.corporateName,
          taxId: c.doc,
          licensePlate: c.licensePlate,
          plateNickname: c.licensePlateNickname,
          contractStatusDescription: c.contractStatusDescription,
        }));
        setItemsPerPage(10)
        setClientList(mapped);
        setTotalPages(response.paging?.totalPages ?? 0); // Atualiza o total de páginas
        setItemsPerPage(10); // Atualiza o tamanho da página
        applyFilters(mapped); // Filtra localmente
      }
    } catch (error) {
      console.log("Erro na busca:.");
      console.log(error);
    }
    finally {
      setIsLoading(false);
    }
  };


  const applyFilters = (list) => {
    const filtered = list.filter((client) => {
      const matchName = filter.clientName
        ? client.socialName?.toLowerCase().includes(filter.clientName.toLowerCase())
        : true;
      
      const matchDocument = filter.document
        ? client.taxId?.replace(/\D/g, '').includes(filter.document.replace(/\D/g, ''))
        : true;
      
      const matchPlate = filter.plate
        ? client.licensePlate?.toLowerCase().includes(filter.plate.toLowerCase())
        : true;
  
      return matchName && matchDocument && matchPlate;
    });
  
    setFilteredList(filtered);
  };
  

  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredList.slice(startIndex, startIndex + itemsPerPage);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= Math.ceil(clientList.length / itemsPerPage)) {
      setCurrentPage(newPage);
    }
  };

  const handleFilterChange = (event) => {
    setFilter({
      name: event.target.name,
      value: event.target.value.trim(),
    });
  };

  const handleDocumentChange = (event) => {
    let value = event.target.value.trim();
    setFilter({
      name: event.target.name,
      value: value,
    });
  };

  const onView = (item) => {
    window.location.href = `/#/clients/details/${item.clientId}`;
  };


  const maxPageButtons = 3;
const maxPages = Math.max(1, Math.ceil(filteredList.length / itemsPerPage));

const startPage = Math.floor((currentPage - 1) / maxPageButtons) * maxPageButtons + 1;
const endPage = maxPages === 1 ? 1 : Math.min(startPage + maxPageButtons - 1, maxPages);


  useEffect(()=>{
    onSearch()
     // eslint-disable-next-line
  },[])

  const formatDocument = (doc) => {
    if (!doc) return "";
  
    // Remove qualquer caractere que não seja número
    const onlyNumbers = doc.replace(/\D/g, "");
  
    if (onlyNumbers.length === 11) {
      // Formata CPF: XXX.XXX.XXX-XX
      return onlyNumbers.replace(
        /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
        "$1.$2.$3-$4"
      );
    } else if (onlyNumbers.length === 14) {
      // Formata CNPJ: XX.XXX.XXX/XXXX-XX
      return onlyNumbers.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
      );
    }
  
    return doc; // Retorna sem alterações se não for CPF nem CNPJ válido
  };
  
  

  return (
    <>
      <div className='container-xxl flex-grow-1 container-p-y'>
        <h4 className='fw-bold py-3 mb-4'>
          <span className='text-muted fw-light'>{t("CLIENT.SEARCH_TITLE")}</span>
        </h4>

        <ToastError show={showToast} closeToast={() => setShowToast(false)} />

        <div className='row'>
          <h5>{t("CLIENT.FILL_FIELDS_TO_FILTER")}</h5>
          <div className='row'>
            <div className='mb-3 col-md-3'>
              <div className='mb-3'>
                <SearchInputField
                  name='clientName'
                  label={t("COMMON.NAME")}
                  type='text'
                  tabIndex='1'
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className='mb-3 col-md-3'>
              <div className='mb-3'>
                <SearchInputDocument
                  name='document'
                  label={t("COMMON.CPF_CNPJ")}
                  type='text'
                  tabIndex='2'
                  onChange={handleDocumentChange}
                />
              </div>
            </div>
            <div className='mb-3 col-md-3'>
              <div className='mb-3'>
                <SearchInputField
                  name='plate'
                  label={t("COMMON.PLATE")}
                  type='text'
                  tabIndex='2'
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            <div className='mb-3 col-md-3 col-lg-3'>
              <div className='d-flex'>
                <div style={{ paddingRight: "5px" }}>
                  <button
                    type='button'
                    className='btn btn-primary'
                    tabIndex={3}
                    // disabled={!filter?.clientName && !filter?.document & !filter?.plate}
                    onClick={onSearch}>
                    <AiOutlineSearch className='icon-in-button' />
                    {t("COMMON.SEARCH")}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>

        {isLoading ? <Spinner /> : ""}

        {filteredList?.length > 0 ? (
         <>
         <div className="card">
              <LongTable data={
                getPaginatedData().map(client => ({
                  ...client,
                  taxId: formatDocument(client.taxId) // Aplica a formatação antes de exibir
                }))
              }
                columns={listColumns} fields={listFieldColumns} onView={onView} />
                {maxPages > 1 && (
  <div className="pagination">
    {/* Botão Anterior */}
    {currentPage > 1 && (
      <button
        className="page-link"
        onClick={() => handlePageChange(currentPage - 1)}
        style={{ padding: "0px", marginLeft: "10px", marginBottom: "12px", marginTop: "0px" }}
      >
        <BiChevronsLeft />
      </button>
    )}

    <nav>
      <ul className="pagination" style={{ paddingLeft: "12px", paddingRight: "12px" }}>
        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((item) => (
          <li key={item} className={`page-item ${currentPage === item ? "active" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(item)}>
              {item}
            </button>
          </li>
        ))}
      </ul>
    </nav>

    {/* Botão Próximo */}
    {currentPage < maxPages && (
      <button
        className="page-link"
        onClick={() => handlePageChange(currentPage + 1)}
        style={{ padding: "0px", marginBottom: "14px", marginTop: "0px" }}
      >
        <BiChevronsRight />
      </button>
    )}
  </div>
)}

         </div>
       </>
        ) : 
        ("")}
      </div>
    </>
  );
};
export default ClientSearch;
